/**
 * API Configuration — MineMiner SPA build
 *
 * sprint-104 (epic-013 Sprint B, step 2d / S5). Forked from `Frontend/`'s
 * config.ts and trimmed to only the surfaces the carved MineMiner.API host
 * exposes:
 *
 *   - `AUTH` block — single login route on the carved host.
 *   - `MINEMINER` / `MINEMINER_PUBLIC` blocks — the full role-gated + public
 *     MineMiner surface (unchanged from core).
 *
 * No `REDDIT_MENTIONS` / `BUSINESSES` / `REGIONS` / `USERS` / `SUBSCRIPTIONS` /
 * `PAYMENTS` / `CUSTOMER_MENTIONS` — those live on the core MentionedMe API
 * and are not reachable from the MineMiner SPA.
 */

/**
 * Environment type
 */
export type Environment = 'development' | 'staging' | 'production';

/**
 * Get current environment from environment variables
 */
export function getEnvironment(): Environment {
  const env = import.meta.env.VITE_ENVIRONMENT as string | undefined;

  if (env === 'staging' || env === 'production') {
    return env;
  }

  return 'development';
}

/**
 * Get the base API URL from environment variables.
 *
 * Production default points at the carved MineMiner App Service (S6 binds
 * `api.mymineminer.com` to this app; pre-bind, the `.azurewebsites.net`
 * hostname is the canonical reachability).
 */
export function getApiBaseUrl(): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL as string | undefined;

  if (!baseUrl) {
    const env = getEnvironment();

    const defaults: Record<Environment, string> = {
      development: 'http://localhost:5000',
      staging: 'https://app-mineminer-api-prod.azurewebsites.net',
      production: 'https://api.mymineminer.com',
    };

    const defaultUrl = defaults[env];
    console.warn(`VITE_API_BASE_URL not set, using default for ${env}: ${defaultUrl}`);
    return defaultUrl;
  }

  return baseUrl;
}

/**
 * Get API timeout from environment variables
 */
export function getApiTimeout(): number {
  const timeout = import.meta.env.VITE_API_TIMEOUT as string | undefined;

  if (timeout) {
    const parsed = parseInt(timeout, 10);
    if (!isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  // Default timeout: 15 minutes (matches core; covers long-running MineMiner
  // exhibitor imports + ingest-run admin operations).
  return 900000;
}

/**
 * Check if running in development mode
 */
export function isDevelopment(): boolean {
  return getEnvironment() === 'development';
}

/**
 * Check if running in production mode
 */
export function isProduction(): boolean {
  return getEnvironment() === 'production';
}

/**
 * API endpoint paths — MineMiner SPA surface
 */
export const API_ENDPOINTS = {
  // Authentication — carved MineMiner host. sprint-107 S1.5 added the
  // forgot-password + reset-password endpoints (Stream A item A2); login is
  // still the only credentialed-issue route.
  AUTH: {
    LOGIN: '/api/mineminer/auth/login',
    // sprint-130 S4 — sliding session: exchange the access+refresh pair for a
    // rotated pair so an actively-used rep app never expires mid-entry.
    REFRESH: '/api/mineminer/auth/refresh',
    FORGOT_PASSWORD: '/api/mineminer/auth/forgot-password',
    RESET_PASSWORD: '/api/mineminer/auth/reset-password',
    // sprint-108 S1 — admin invites a new rep / admin; anonymous accept-invite
    // redeems the tokenized link from the MM_RepInvite email.
    INVITE_USER: '/api/mineminer/auth/admin/invite-user',
    ACCEPT_INVITE: '/api/mineminer/auth/accept-invite',
    // sprint-108 S2 — auth-gated rotation of a signed-in user's own password.
    CHANGE_PASSWORD: '/api/mineminer/auth/change-password',
  },

  // MineMiner (Epic-006 + epic-011): health probe + role-gated data-review,
  // CRM, leads, campaigns, reports, audit endpoints. Forked unchanged from
  // the core SPA so the existing MineMinerRepository / useMineMiner queries
  // work as-is.
  // sprint-217 (epic-036 Phase 1 increment 1D, goal 3) — the CLIENT area.
  //
  // A separate group from MINEMINER below, matching the separate controller: everything here
  // is a customer reading their own mine's data, and everything there is internal staff
  // prospecting. Keeping the two apart in one object is what stops a client page reaching for
  // a staff endpoint by autocomplete — it would 403, but only at runtime, and only for a user
  // who is not internal staff.
  MINE_CLIENT: {
    MY_MINES: '/api/mineminer/client/mines',
    MINE_BY_ID: (mineId: string): string => `/api/mineminer/client/mines/${mineId}`,
    MINE_VIOLATIONS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/violations`,
    /**
     * One citation as the §5.9 pair — MSHA's published record and the operator's filed response,
     * separately nullable (sprint-229, 4A/N3/U2 — the sprint's one API addition). Keyed by the
     * violation NUMBER off the paperwork, which both lists serve, so a pre-ingest
     * operator-entered citation resolves here too. 404 when neither side knows the number.
     */
    MINE_VIOLATION_DETAIL: (mineId: string, violationNo: string): string =>
      `/api/mineminer/client/mines/${mineId}/violations/${encodeURIComponent(violationNo)}`,
    MINE_INSPECTION_EVENTS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/inspection-events`,
    MINE_EVIDENCE: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence`,
    // goal 4 — the 7000-3 attach. The POST names the inspection event in the ROUTE because that
    // is what the server checks belongs to the mine; putting it in the body would move the
    // cross-tenant question somewhere nothing validates it.
    MINE_FORM_7000_3: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/7000-3`,
    ATTACH_FORM_7000_3: (mineId: string, inspectionEventId: string): string =>
      `/api/mineminer/client/mines/${mineId}/inspection-events/${inspectionEventId}/7000-3`,
    // goals 4b+5 — the citation pipeline. Both are POSTs on an attachment already proven to
    // belong to this mine; the attachment id is NOT covered by the route's membership gate, so
    // the server resolves it through the mine before reading a byte.
    EXTRACT_FORM_7000_3: (mineId: string, attachmentId: string): string =>
      `/api/mineminer/client/mines/${mineId}/7000-3/${attachmentId}/extract`,
    CONFIRM_CLIENT_CITATION: (mineId: string, attachmentId: string): string =>
      `/api/mineminer/client/mines/${mineId}/7000-3/${attachmentId}/confirm`,

    // ── Phase 3 (sprint-227, increment 3F) — the abatement surface ───────────────────────────
    //
    // 🔴 Every id below is a PATH SEGMENT rather than a query parameter, and that is a security
    // property rather than a URL-style preference. MineMembershipRequirement reads the `mineId`
    // ROUTE value and resolves an active membership before the endpoint answers; each handler
    // then resolves the SECOND id (task, pushback, step, notification) THROUGH that mine before
    // acting. Keeping the SPA's shape aligned with the API's means the thing the user is looking
    // at and the thing the server authorised are the same string.
    //
    // These endpoints have existed since increments 3A–3E and nothing in the browser could reach
    // them: 3B, 3C, 3D and 3E all shipped API-only (TD-233). This block is where that ends.

    /** The mine's abatement tasks, paged. */
    MINE_TASKS: (mineId: string): string => `/api/mineminer/client/mines/${mineId}/tasks`,

    /** One task in full — evidence, the pushback chain, and both deadlines (sprint-227). */
    MINE_TASK: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}`,

    /** One task's pushback episodes with their ordered resolution chains. */
    MINE_TASK_PUSHBACKS: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/pushbacks`,

    // 🔴 The roster, and the reason an Admin can pick a Supervisor in a browser at all
    // (sprint-227). Every task DTO serves `assignedToUserId` as a bare id, so this is also the
    // only way any surface renders an assignee as a person. Roster, never a user directory: it
    // is scoped by the route's mine, and revoked members are SERVED rather than filtered, because
    // a task assigned to somebody who has left still names them.
    MINE_MEMBERSHIPS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/memberships`,

    REVOKE_MINE_MEMBERSHIP: (mineId: string, userId: string, role: string): string =>
      `/api/mineminer/client/mines/${mineId}/memberships/${userId}/${role}/revoke`,

    // 🔴 THE READ THAT MAKES THE ASSIGN BELOW CALLABLE FROM A BROWSER (TD-235, sprint-227/S003).
    // The assign route names `{violationResponseId}` and, until this endpoint, nothing served one:
    // `MineViolationDto` has no such field and the id was returned exactly once, by the confirm
    // response, where it is also legitimately null. So the assign hook was written, correct, and
    // uncallable.
    //
    // ⚠️ NOT the same thing as MINE_VIOLATIONS above, and the difference is the point. That is
    // MSHA's published record, ingest-owned and read-only (DR-003). This is what the OPERATOR
    // filed, seeded at confirm time from the 7000-3 in their hand — days or weeks before the
    // weekly ingest publishes the citation. Work is assigned against the response, so the freshest
    // citations are assignable precisely because this list is not the violations list.
    MINE_VIOLATION_RESPONSES: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/violation-responses`,

    // The assign names the VIOLATION RESPONSE in the route, not the task — the task does not
    // exist yet, and the response is what it hangs off. There is no reassign endpoint: assigning
    // IS creating, and Admin-only.
    ASSIGN_ABATEMENT_TASK: (mineId: string, violationResponseId: string): string =>
      `/api/mineminer/client/mines/${mineId}/violation-responses/${violationResponseId}/tasks`,

    // 🔴 Two calls, and they are separate on purpose (sprint-227). The upload stores bytes and
    // returns the reference; the completion makes the assertion and REQUIRES at least one such
    // reference. Before the upload existed, a Supervisor could not complete a task in a browser at
    // all — the completion accepts references to bytes already in the private evidence container,
    // which the Phase 2 field device puts there on capture and no browser ever could.
    UPLOAD_TASK_EVIDENCE: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/evidence`,

    COMPLETE_ABATEMENT_TASK: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/complete`,

    // ── sprint-237 (4E-1 Goal 6) — evidence integrity and legal holds ────────────────────────
    //
    // 🔴 Both blocks are mine-scoped in the ROUTE for the reason the whole 3F block above gives:
    // MineMembershipRequirement reads the `mineId` route value and resolves an active membership
    // before the endpoint answers, and each handler then resolves the second id THROUGH that mine.
    //
    // ⚠️ Reads sit on the client policy; PLACE and CLEAR are Admin-only, server-side. The SPA's
    // conditional rendering is convenience, never the control.

    /** Fingerprint, verification state, retention floor and derived hold state for one file. */
    EVIDENCE_BLOB_PROVENANCE: (mineId: string, evidenceBlobId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-blobs/${evidenceBlobId}`,

    /**
     * The same record for a SET of files in one round trip (sprint-254, A6) — the batch the task
     * page's accepted-cost note named as its own fix. An id the caller may not see is simply
     * absent from the response map, never an error.
     */
    EVIDENCE_BLOB_PROVENANCE_BATCH: (mineId: string, evidenceBlobIds: string[]): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-blobs?${evidenceBlobIds
        .map((id) => `ids=${encodeURIComponent(id)}`)
        .join('&')}`,

    // ── sprint-244 (4E-2 Goal 5, DR-040) — the read path ────────────────────────────────────
    //
    // 🔴 This is the endpoint that lets anyone OPEN a piece of evidence. Until it existed, an
    // abatement photograph could not be retrieved by any credential — not the Supervisor who
    // filed it, not the Admin who has to send it to an MSHA inspector. Same client policy as the
    // provenance read above, for the same stated reason.
    //
    // ⚠️ The response's `sasUrl` IS a credential — anyone holding the string can fetch the bytes
    // until it expires. Never log it, never put it in a query string of ours, never persist it.

    /** A short-lived URL that opens one file, plus its verification state at mint time. */
    EVIDENCE_BLOB_READ_LINK: (mineId: string, evidenceBlobId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-blobs/${evidenceBlobId}/read-link`,

    /**
     * sprint-276 (TD-255, S001/D4) — the bytes of one IMAGE, streamed by the API so the signed URL
     * never reaches the browser. Audited server-side exactly like a read-link mint; 415 for a
     * non-image.
     */
    EVIDENCE_BLOB_IMAGE_CONTENT: (mineId: string, evidenceBlobId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-blobs/${evidenceBlobId}/content`,

    // ── sprint-269 (S005) — the inspection triage surface: this app's FIRST /api/v2 reads ──
    //
    // v2 because the endpoints are new and every new server surface is additive v2 (epic-042
    // R5); v1 stays byte-frozen. Same path-segment security property as everything above —
    // the route's mineId is what the membership policy proves.

    /**
     * The inspection list as v2 serves it (S006/D1): newest DECLARED first — during a live
     * walk the event being watched is the one just opened, and same-day BeginDates (the
     * multi-supervisor norm; web declares are date-only) tie under the v1 ordering.
     */
    INSPECTION_EVENTS_V2: (mineId: string): string =>
      `/api/v2/mineminer/inspections/mines/${mineId}/inspection-events`,

    /** The triage tree: stops → observations (issues) → evidence, urgent-first server-side. */
    INSPECTION_TRIAGE: (mineId: string, inspectionEventId: string): string =>
      `/api/v2/mineminer/inspections/mines/${mineId}/inspection-events/${inspectionEventId}/triage`,

    /**
     * The pre-citation assign (S005/D6): open abatement work from an inspection-day
     * observation, BEFORE the 7000-3 exists. The task anchors to the observation and
     * re-anchors to the violation response when the citation lands. Admin-gated server-side.
     */
    ASSIGN_TASK_FROM_OBSERVATION: (mineId: string, observationId: string): string =>
      `/api/v2/mineminer/mines/${mineId}/observations/${observationId}/tasks`,

    /**
     * sprint-277/S002/D1–D3 — the Tasks-tab assign with no citation and no capture: pre-citation
     * or proactive work. Admin-gated server-side; `kind` is required in the body.
     */
    ASSIGN_NO_CITATION_TASK: (mineId: string): string =>
      `/api/v2/mineminer/mines/${mineId}/tasks`,

    /**
     * sprint-277/S002/D4 — the Admin links a pre-citation or proactive task to its citation; the
     * kind becomes whatever the citation's routing allows. 409 if already linked, 422 if the
     * document takes no work.
     */
    LINK_TASK_TO_CITATION: (mineId: string, abatementTaskId: string): string =>
      `/api/v2/mineminer/mines/${mineId}/tasks/${abatementTaskId}/link-citation`,

    // ── sprint-244 (4E-2 Goal 4) — the evidence-integrity register ──────────────────────────
    //
    // 🔴 Admin-only server-side, unlike the two reads above. Every row's next act is an Admin's,
    // and a queue a Supervisor cannot act on is noise on a field device.

    /** Files whose verification failed, plus everything under an active legal hold. Paged. */
    MINE_EVIDENCE_INTEGRITY: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-integrity`,

    // 🔴 Its own request rather than a field on the paged register — a standing fact folded into
    // page 1 of a list vanishes on page 2.
    /** Whether the scheduled checker has run, and whether it is reaching this mine. */
    MINE_EVIDENCE_SWEEP_STATUS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-integrity/sweep-status`,

    // ── sprint-244 (4E-2 Goal 7) — who a violation-response packet is addressed to ──────────
    //
    // 🔴 Admin-only server-side. Confirming who issued a citation and recording how to reach them
    // are Admin acts; a Supervisor in the field has nothing to do with either.
    //
    // ⚠️ The identity behind these routes is SHARED across operators — an AR number and an office
    // code are national MSHA identifiers, and "confirm once" cannot hold on a mine-scoped row. The
    // `{mineId}` in every path scopes the CONTACT DETAILS, which are the operator's own.

    /** Inspectors this mine's citations resolve to, the sightings that resolve to nobody, and its field offices. */
    MINE_CORRESPONDENCE_DIRECTORY: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/directory`,

    /** POST adds somebody new to the shared directory. 409 when the AR number is already held. */
    CORRESPONDENCE_INSPECTORS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/inspectors`,

    /** POST binds an observed spelling to an inspector — the answer that is never asked again. */
    CORRESPONDENCE_INSPECTOR_ALIASES: (mineId: string, inspectorId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/inspectors/${inspectorId}/aliases`,

    /** PUT saves this mine's contact details for an inspector. */
    CORRESPONDENCE_INSPECTOR_CONTACT: (mineId: string, inspectorId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/inspectors/${inspectorId}/contact`,

    /** PUT saves this mine's postal details for a field office. There is deliberately no create route. */
    CORRESPONDENCE_FIELD_OFFICE_CONTACT: (mineId: string, fieldOfficeId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/field-offices/${fieldOfficeId}/contact`,

    // ── sprint-244/S005 (4E-2 Goal 7 part 2) — the envelope; sprint-249/S001 (4F) — the send
    //    and the reply.

    /** GET lists the mine's draft packets (paged); POST opens one. */
    CORRESPONDENCE_PACKETS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/packets`,

    /** GET the whole envelope; PUT edits it; DELETE discards it. */
    CORRESPONDENCE_PACKET: (mineId: string, packetId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/packets/${packetId}`,

    /** GET what may go in the envelope — the mine's abatement evidence with an integrity record. */
    CORRESPONDENCE_PACKET_CANDIDATE_EVIDENCE: (mineId: string, packetId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/packets/${packetId}/candidate-evidence`,

    /** POST links one piece of evidence. 200 with warnings — never a refusal — on unreviewed or mismatched evidence. */
    CORRESPONDENCE_PACKET_EVIDENCE: (mineId: string, packetId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/packets/${packetId}/evidence`,

    /** DELETE unlinks one piece of evidence. The bytes and their record are untouched. */
    CORRESPONDENCE_PACKET_EVIDENCE_ITEM: (mineId: string, packetId: string, evidenceBlobId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/packets/${packetId}/evidence/${evidenceBlobId}`,

    /** POST marks a Draft sent (outside the product, D-b) with channel + proof; opens the packet's feed watch. 409 off Draft. */
    CORRESPONDENCE_PACKET_SEND: (mineId: string, packetId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/packets/${packetId}/send`,

    /** POST records MSHA's reply (Accepted | Rejected) with the reply attached. 409 unless the packet is Sent. */
    CORRESPONDENCE_PACKET_REPLY: (mineId: string, packetId: string): string =>
      `/api/mineminer/client/mines/${mineId}/correspondence/packets/${packetId}/reply`,

    /** POST places a hold. */
    EVIDENCE_HOLDS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-holds`,

    /** The scopes a hold may name — drives the picker rather than a hardcoded list. */
    EVIDENCE_HOLD_SCOPES: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-holds/scopes`,

    /** Every hold ever placed on one scope, cleared ones included (the append-only history). */
    EVIDENCE_HOLD_HISTORY: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-holds/history`,

    // 🔴 DELETE, and it takes a REQUIRED BODY — the stated reason DR-038 demands when clearing,
    // exactly as when placing. Send it as axios `{ data: { reason } }`.
    EVIDENCE_HOLD_BY_ID: (mineId: string, evidenceLegalHoldId: string): string =>
      `/api/mineminer/client/mines/${mineId}/evidence-holds/${evidenceLegalHoldId}`,

    // sprint-228 (F5): the middle outcome — evidence filed without a status change.
    REPORT_TASK_PROGRESS: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/progress`,

    // Returning work is the assignee's call; putting it back is the Admin's.
    PUSH_BACK_ABATEMENT_TASK: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/push-back`,

    REISSUE_ABATEMENT_TASK: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/reissue`,

    // sprint-277 S007/D6: the Admin withdraws open work, with a required reason.
    WITHDRAW_ABATEMENT_TASK: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/withdraw`,

    // sprint-230 (4B): the Admin answers a filed completion claim — accept for submission, or
    // reject with the reason. Admin-only, like assign and reissue; a rejection is a distinct act,
    // never a pushback (DR-030).
    REVIEW_ABATEMENT_TASK: (mineId: string, abatementTaskId: string): string =>
      `/api/mineminer/client/mines/${mineId}/tasks/${abatementTaskId}/review`,

    // A step is posted to the EPISODE rather than to the task: a task pushed back twice has two
    // chains, and a step posted to the task would have to guess which one it belongs to.
    ADD_TASK_RESOLUTION_STEP: (mineId: string, abatementTaskPushbackId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-pushbacks/${abatementTaskPushbackId}/steps`,

    UPDATE_TASK_RESOLUTION_STEP: (mineId: string, taskResolutionStepId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-resolution-steps/${taskResolutionStepId}/status`,

    // ── sprint-279 (F23 slices 2 + 5): outside contacts on a resolution step ───────────────
    // Contacts and requests are Admin-only server-side (and need a linked organization — 404
    // otherwise); the ETA edit and the timeline read are any client member.
    EXTERNAL_CONTACTS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/external-contacts`,

    DEACTIVATE_EXTERNAL_CONTACT: (mineId: string, externalContactId: number): string =>
      `/api/mineminer/client/mines/${mineId}/external-contacts/${externalContactId}/deactivate`,

    EXTERNAL_STEP_REQUEST: (mineId: string, taskResolutionStepId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-resolution-steps/${taskResolutionStepId}/external-request`,

    EXTERNAL_STEP_REQUEST_RESEND: (mineId: string, taskResolutionStepId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-resolution-steps/${taskResolutionStepId}/external-request/resend`,

    EXTERNAL_STEP_REQUEST_REVOKE: (mineId: string, taskResolutionStepId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-resolution-steps/${taskResolutionStepId}/external-request/revoke`,

    /** POST that writes nothing: renders the request email for the Admin to read first. */
    EXTERNAL_STEP_REQUEST_PREVIEW: (mineId: string, taskResolutionStepId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-resolution-steps/${taskResolutionStepId}/external-request/preview`,

    /** Moves the step's ESTIMATE only — never MSHA's date. */
    TASK_RESOLUTION_STEP_ETA: (mineId: string, taskResolutionStepId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-resolution-steps/${taskResolutionStepId}/eta`,

    TASK_RESOLUTION_STEP_ACTIVITY: (mineId: string, taskResolutionStepId: string): string =>
      `/api/mineminer/client/mines/${mineId}/task-resolution-steps/${taskResolutionStepId}/activity`,

    /** This user's notifications at this mine, paged, each with its per-channel delivery state. */
    MINE_NOTIFICATIONS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/notifications`,

    MINE_NOTIFICATIONS_UNREAD_COUNT: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/notifications/unread-count`,

    // 🔴 A POST, and not a side effect of listing. What it writes is the only proof-of-receipt the
    // platform has — email stops at Delivered (DR-029) and Push has no client — so the escalation
    // sweep that warns an Admin their Supervisor never came back reads that column and nothing
    // else. Marking read on list would mean nothing is ever escalated.
    MARK_NOTIFICATION_READ: (mineId: string, notificationId: string): string =>
      `/api/mineminer/client/mines/${mineId}/notifications/${notificationId}/read`,

    /** The mine's internal-deadline ladder (increment 3E). GET reads it, POST sets it. */
    INTERNAL_DEADLINE_POLICY: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/internal-deadline-policy`,

    // sprint-231 (4C): the reconciliation queue — every expectation the product has asserted
    // about this mine's citations and where each stands against MSHA's weekly feed. Admin-only
    // on the server; the Mismatch rows are the durable to-dos (§5.8).
    MINE_EXPECTED_STATES: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/expected-states`,

    // The §5.8 human exit — Admin-only, note required. The only way a Mismatch closes other
    // than a later feed publishing the awaited fact.
    RESOLVE_EXPECTED_STATE: (mineId: string, expectedStateId: string): string =>
      `/api/mineminer/client/mines/${mineId}/expected-states/${expectedStateId}/resolve`,

    // sprint-236/S003 (4D): the held-citation register — the enforcement documents nobody can
    // currently work, plus the ones a later reading of MSHA's record moved. Admin-only.
    // 🔴 4D's ONLY new API surface (charter §4.4: zero new write endpoints, one new read).
    MINE_HELD_CITATIONS: (mineId: string): string =>
      `/api/mineminer/client/mines/${mineId}/held-citations`,

    // sprint-252 (4H, DR-041): the Legal portfolio lane — SELF-SCOPED, so these are plain
    // strings like MY_MINES rather than mineId functions: the server resolves the caller's
    // own active Legal grants and reads nothing outside them. Legal-only server-side
    // (RequireMineClientLegalPortfolio); everyone else's request 403s.
    /** Every mine in the caller's Legal portfolio, with per-mine violation roll-ups. */
    PORTFOLIO_MINES: '/api/mineminer/client/portfolio/mines',
    /** Trends v1 — the portfolio's published violations by calendar quarter (?quarters=8). */
    PORTFOLIO_VIOLATION_TRENDS: '/api/mineminer/client/portfolio/violation-trends',
  },

  MINEMINER: {
    HEALTH: '/api/mineminer/health',
    MINES: '/api/mineminer/mines',
    MINE_BY_ID: (mineId: string): string => `/api/mineminer/mines/${mineId}`,
    MINE_VIOLATIONS: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/violations`,
    MINE_INSPECTIONS: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/inspections`,
    // Sprint-241 (epic-039 increment D) — the accidents section.
    MINE_ACCIDENTS: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/accidents`,
    // Sprint-233 — EventNo is a paper-printed key; encode it in the path.
    MINE_INSPECTION_DETAIL: (mineId: string, eventNo: string): string =>
      `/api/mineminer/mines/${mineId}/inspections/${encodeURIComponent(eventNo)}`,
    MINE_ASSESSED_VIOLATIONS: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/assessed-violations`,
    MINE_IMMINENT_DANGER_ORDERS: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/imminent-danger-orders`,
    MINE_CONTROLLER_OPERATOR_HISTORY: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/controller-operator-history`,
    MINE_PRODUCTION_YEARLY: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/production-yearly`,
    MINE_PRODUCTION_QUARTERLY: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/production-quarterly`,
    MINE_CONTRACTOR_PRODUCTION: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/contractor-production`,
    // sprint-148 follow-on: a mine's contractor roster from the DOL contractor-history tenure
    // (surfaces uncited contractors). `?activeOnly=` toggles full roster vs on-site-now.
    MINE_CONTRACTORS: (mineId: string): string =>
      `/api/mineminer/mines/${mineId}/contractors`,
    VIOLATIONS: '/api/mineminer/violations',
    VIOLATIONS_ENFORCEMENT_AREAS: '/api/mineminer/violations/enforcement-areas',
    VIOLATIONS_AGGREGATE_OCC: '/api/mineminer/violations/aggregate-occ',
    INGEST_RUNS: '/api/mineminer/ingest-runs',
    LEADS_NEW_MINE: '/api/mineminer/leads/new-mine',
    LEADS_RECENT_CITATION: '/api/mineminer/leads/recent-citation',
    LEADS_IMMINENT: '/api/mineminer/leads/imminent',
    LEAD_DETAIL: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}`,
    LEAD_ASSIGNMENT: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/assignment`,
    LEAD_STATUS: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/status`,
    LEAD_NOTES_LIST: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/notes`,
    LEAD_CONTACT_LOG_LIST: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/contact-log`,
    LEAD_ASSIGN_POST: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/assign`,
    LEAD_STATUS_POST: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/status`,
    LEAD_NOTES_POST: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/notes`,
    LEAD_CONTACT_POST: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/contact`,
    LEAD_CONVERT_POST: (signal: string, leadId: string): string =>
      `/api/mineminer/leads/${signal}/${leadId}/convert`,
    ME: '/api/mineminer/me',
    ADMIN_CONFIG: '/api/mineminer/admin/config',
    ADMIN_CONFIG_KEY: (key: string): string =>
      `/api/mineminer/admin/config/${encodeURIComponent(key)}`,
    ADMIN_SCORING_RERUN: (signal: 'a' | 'b' | 'c'): string =>
      `/api/mineminer/admin/scoring/rerun-${signal}`,
    ADMIN_CRM_USERS: '/api/mineminer/admin/crm-users',
    // sprint-108 S1 fold-in — full MineMinerUser lifecycle (InvitePending /
    // InviteExpired / AcceptedUnlinked / LinkedActive / LinkedInactive). Powers
    // the Reps tab table (replaces ADMIN_CRM_USERS as the data source there;
    // ADMIN_CRM_USERS remains for any future callers).
    ADMIN_USERS: '/api/mineminer/admin/users',
    ADMIN_CRM_USERS_DISABLE: (id: string): string =>
      `/api/mineminer/admin/crm-users/${id}/disable`,
    ADMIN_ELIGIBLE_APPLICATION_USERS:
      '/api/mineminer/admin/crm-users/eligible-application-users',
    OPERATORS: '/api/mineminer/operators',
    OPERATOR_BY_ID: (operatorId: string): string =>
      `/api/mineminer/operators/${operatorId}`,
    OPERATOR_MINES: (operatorId: string): string =>
      `/api/mineminer/operators/${operatorId}/mines`,
    OPERATOR_VIOLATIONS: (operatorId: string): string =>
      `/api/mineminer/operators/${operatorId}/violations`,
    OPERATOR_TENURE: (operatorId: string): string =>
      `/api/mineminer/operators/${operatorId}/tenure`,
    // sprint-148 follow-on: the operator's contractor roster (DOL tenure) across its mines.
    OPERATOR_CONTRACTORS: (operatorId: string): string =>
      `/api/mineminer/operators/${operatorId}/contractors`,
    CONTROLLERS: '/api/mineminer/controllers',
    CONTROLLER_BY_ID: (controllerId: string): string =>
      `/api/mineminer/controllers/${controllerId}`,
    CONTROLLER_MINES: (controllerId: string): string =>
      `/api/mineminer/controllers/${controllerId}/mines`,
    CONTROLLER_VIOLATIONS: (controllerId: string): string =>
      `/api/mineminer/controllers/${controllerId}/violations`,
    CONTROLLER_HISTORY: (controllerId: string): string =>
      `/api/mineminer/controllers/${controllerId}/history`,
    CONTROLLER_OPERATORS_UNDER: (controllerId: string): string =>
      `/api/mineminer/controllers/${controllerId}/operators-under`,
    // sprint-148 follow-on: the controller's contractor roster (DOL tenure) across its mines.
    CONTROLLER_CONTRACTORS: (controllerId: string): string =>
      `/api/mineminer/controllers/${controllerId}/contractors`,
    CONTRACTORS: '/api/mineminer/contractors',
    CONTRACTOR_BY_ID: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}`,
    CONTRACTOR_MINES: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}/mines`,
    // sprint-148 SESSION_004: the company's DOL tenure grouped by mine ("MINES ON-SITE").
    CONTRACTOR_TENURE: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}/tenure`,
    CONTRACTOR_VIOLATIONS: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}/violations`,
    CONTRACTOR_PRODUCTION_YEARLY: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}/production-yearly`,
    CONTRACTOR_PRODUCTION_QUARTERLY: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}/production-quarterly`,
    OPERATOR_ASSESSED_VIOLATIONS: (operatorId: string): string =>
      `/api/mineminer/operators/${operatorId}/assessed-violations`,
    CONTROLLER_ASSESSED_VIOLATIONS: (controllerId: string): string =>
      `/api/mineminer/controllers/${controllerId}/assessed-violations`,
    CONTRACTOR_ASSESSED_VIOLATIONS: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}/assessed-violations`,
    CONTACTS: '/api/mineminer/contacts',
    CONTACT_BY_ID: (contactId: string): string =>
      `/api/mineminer/contacts/${contactId}`,
    CONTACT_LINKS: (contactId: string): string =>
      `/api/mineminer/contacts/${contactId}/links`,
    CONTACT_MARKETING_OPT_OUT: (contactId: string): string =>
      `/api/mineminer/contacts/${contactId}/marketing-opt-out`,
    CONTACT_LINK_CREATE: (
      contactId: string,
      entityType: string,
      entityId: number,
    ): string =>
      `/api/mineminer/contacts/${contactId}/links/${entityType}/${entityId}`,
    CONTACT_LINK_DELETE: (
      contactId: string,
      entityType: string,
      linkId: string,
    ): string =>
      `/api/mineminer/contacts/${contactId}/links/${entityType}/${linkId}`,
    ENTITY_CONTACTS: (entityType: string, entityId: number): string =>
      `/api/mineminer/${entityType}/${entityId}/contacts`,
    CONTACT_IMPORT_INSPECT: '/api/mineminer/contacts:import-inspect',
    CONTACT_IMPORT_PREVIEW: '/api/mineminer/contacts:import-preview',
    CONTACT_IMPORT_COMMIT: (entityType: string, entityId: number): string =>
      `/api/mineminer/${entityType}/${entityId}/contacts:import-commit`,
    MANUAL_CONTRACTOR_ARCHIVE: (contractorId: string): string =>
      `/api/mineminer/contractors/${contractorId}/archive`,
    CONTACT_TIMELINE: (contactId: string): string =>
      `/api/mineminer/contacts/${contactId}/timeline`,
    ENTITY_TIMELINE: (kind: string, fk: number): string =>
      `/api/mineminer/entities/${kind}/${fk}/timeline`,
    EVENT_TIMELINE: (eventId: string): string =>
      `/api/mineminer/events/${eventId}/timeline`,
    ACTIVITY_BY_KIND: (kind: string): string =>
      `/api/mineminer/activities/${kind}`,
    ACTIVITY_BY_KIND_ID: (kind: string, id: string): string =>
      `/api/mineminer/activities/${kind}/${id}`,
    ACTIVITY_COMPLETE_CALL: (id: string): string =>
      `/api/mineminer/activities/calls/${id}/complete`,
    ACTIVITY_COMPLETE_MEETING: (id: string): string =>
      `/api/mineminer/activities/meetings/${id}/complete`,
    EVENTS: '/api/mineminer/events',
    EVENT_BY_ID: (eventId: string): string =>
      `/api/mineminer/events/${eventId}`,
    EVENT_CONTACTS: (eventId: string): string =>
      `/api/mineminer/events/${eventId}/contacts`,
    EVENT_CONTACT_LINK_BY_ID: (eventId: string, linkId: string): string =>
      `/api/mineminer/events/${eventId}/contacts/${linkId}`,
    EVENT_EXHIBITORS_IMPORT: (eventId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitors:import`,
    EVENT_EXHIBITORS: (eventId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitors`,
    // sprint-129 — persistent, resumable exhibitor import work-queue.
    EVENT_EXHIBITOR_IMPORTS: (eventId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitor-imports`,
    EVENT_EXHIBITOR_IMPORT_BY_ID: (eventId: string, batchId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitor-imports/${batchId}`,
    EVENT_EXHIBITOR_IMPORT_ROWS: (eventId: string, batchId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitor-imports/${batchId}/rows`,
    EVENT_EXHIBITOR_IMPORT_ROW_CONFIRM: (eventId: string, batchId: string, rowId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitor-imports/${batchId}/rows/${rowId}:confirm`,
    EVENT_EXHIBITOR_IMPORT_ROW_SKIP: (eventId: string, batchId: string, rowId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitor-imports/${batchId}/rows/${rowId}:skip`,
    EXHIBITORS_BY_ENTITY: '/api/mineminer/exhibitors',
    EVENT_EXHIBITOR_MATCH: (eventId: string, exhibitorId: string): string =>
      `/api/mineminer/events/${eventId}/exhibitors/${exhibitorId}/match`,
    ENTITY_SEARCH: '/api/mineminer/entities/search',

    // sprint-239 (epic-039 Canary increment A) — organization tenancy.
    //
    // 🔴 Two families, and the split is the security boundary, not tidiness. The ADMIN_*
    // endpoints span every tenant and answer only to a MineMiner admin; the ORG_* ones are
    // scoped by the organization in their path and answer to that organization's own members.
    // Do not call an ADMIN_* endpoint from an organization-facing screen.
    ADMIN_ORGANIZATIONS: '/api/mineminer/admin/organizations',
    ADMIN_ORGANIZATION_BY_ID: (organizationId: number): string =>
      `/api/mineminer/admin/organizations/${organizationId}`,
    ADMIN_ORGANIZATION_MEMBERS: (organizationId: number): string =>
      `/api/mineminer/admin/organizations/${organizationId}/members`,
    ADMIN_ORGANIZATION_MEMBER_REVOKE: (
      organizationId: number,
      userId: string,
      role: string
    ): string =>
      `/api/mineminer/admin/organizations/${organizationId}/members/${userId}/${encodeURIComponent(role)}`,
    ADMIN_ORGANIZATION_LICENSE: (organizationId: number, feature: string): string =>
      `/api/mineminer/admin/organizations/${organizationId}/licenses/${encodeURIComponent(feature)}`,
    // sprint-279 S002 (epic-039/DR-018) — organization↔mine links. Admin-only. A link scopes the
    // organization's data to the mine and never grants anyone access to it.
    ADMIN_ORGANIZATION_MINES: (organizationId: number, includeEnded = false): string =>
      `/api/mineminer/admin/organizations/${organizationId}/mines${includeEnded ? '?includeEnded=true' : ''}`,
    ADMIN_ORGANIZATION_MINE_UNLINK: (organizationId: number, mineId: string): string =>
      `/api/mineminer/admin/organizations/${organizationId}/mines/${encodeURIComponent(mineId)}`,
    ADMIN_ORGANIZATION_FEATURES: '/api/mineminer/admin/organizations/features',
    ADMIN_ORGANIZATION_ROLES: '/api/mineminer/admin/organizations/roles',

    // sprint-240 (epic-039 Canary increment B) — watch lists.
    //
    // 🔴 THE WRITE PATH IS ADMIN-ONLY, and that is the product decision (epic-039 D12), not a
    // gap. There is no organization-scoped POST or DELETE below, and adding one is a ruling.
    ADMIN_ORGANIZATION_WATCHES: (organizationId: number): string =>
      `/api/mineminer/admin/organizations/${organizationId}/watches`,
    /**
     * Stop watching. 🔴 Removes the whole logical watch by default (epic-039/DR-009 clause 1);
     * `singleId` removes just this MSHA id out of a fanned-out contractor watch, which is the
     * correction path for a wrong name merge and is NOT undone by re-adding the company
     * (epic-039/DR-011).
     */
    ADMIN_ORGANIZATION_WATCH_REMOVE: (
      organizationId: number,
      kind: string,
      naturalKey: string,
      singleId = false
    ): string =>
      `/api/mineminer/admin/organizations/${organizationId}/watches/${encodeURIComponent(kind)}/${encodeURIComponent(naturalKey)}${singleId ? '?scope=row' : ''}`,
    ADMIN_ORGANIZATION_WATCH_KINDS: '/api/mineminer/admin/organizations/watch-kinds',

    /** The caller's own organizations. Names no organization — it is the scope query. */
    ORGANIZATIONS_MINE: '/api/mineminer/organizations',
    ORGANIZATION_HOME: (organizationId: number): string =>
      `/api/mineminer/organizations/${organizationId}`,
    ORGANIZATION_CANARY: (organizationId: number): string =>
      `/api/mineminer/organizations/${organizationId}/canary`,
    /** sprint-240 — the organization's own watch list. 🔴 Read-only; see D12 above. */
    ORGANIZATION_CANARY_WATCHES: (organizationId: number): string =>
      `/api/mineminer/organizations/${organizationId}/canary/watches`,
    /**
     * sprint-242 (epic-039 increment C1) — the organization change feed: what MSHA published
     * at the accounts they track. 🔴 Read-only for the same reason the watch list is: a change
     * record is a derived statement about MSHA's published record, not something a tenant edits.
     */
    ORGANIZATION_CANARY_CHANGES: (organizationId: number, limit = 50): string =>
      `/api/mineminer/organizations/${organizationId}/canary/changes?limit=${limit}`,
    /** sprint-242 — the staff view of the SAME rows. DR-006 clause 4: both audiences, same content. */
    ADMIN_ORGANIZATION_CHANGES: (organizationId: number, limit = 50): string =>
      `/api/mineminer/admin/organizations/${organizationId}/changes?limit=${limit}`,
    /**
     * sprint-267 (epic-039 increment E) — the admin REVIEW QUEUE: one flag per watched entity
     * that changed this week (however many organizations watch it) plus "MSHA may have published
     * a new id for this company" suggestions, each resolved with a note. 🔴 ADMIN family: a flag
     * is per entity, not per organization, so there is no ORG_* twin and must not be one.
     */
    ADMIN_CANARY_REVIEW_FLAGS: (openOnly = true, limit = 100): string =>
      `/api/mineminer/admin/canary/review-flags?openOnly=${openOnly}&limit=${limit}`,
    ADMIN_CANARY_REVIEW_FLAG_OPEN_COUNT: '/api/mineminer/admin/canary/review-flags/open-count',
    ADMIN_CANARY_REVIEW_FLAG_RESOLVE: (flagId: number): string =>
      `/api/mineminer/admin/canary/review-flags/${flagId}/resolve`,
    // sprint-118 (epic-017 Sprint B) account grouping-layer endpoints — consumed by
    // the sprint-119 <EntityPicker> + inline create-and-associate flow.
    ACCOUNT_SEARCH: '/api/mineminer/accounts/search',
    ACCOUNTS: '/api/mineminer/accounts',
    CONTACT_ACCOUNT_LINK_CREATE: (contactId: string, accountId: number): string =>
      `/api/mineminer/contacts/${contactId}/accounts/${accountId}`,
    LISTS: '/api/mineminer/lists',
    // Sprint-133 (epic-020 Sprint D): materialize a scored work-queue slice into a List.
    LISTS_FROM_WORK_QUEUE: '/api/mineminer/lists/from-work-queue',
    LIST_BY_ID: (listId: string): string => `/api/mineminer/lists/${listId}`,
    LIST_MEMBERS: (listId: string): string =>
      `/api/mineminer/lists/${listId}/members`,
    LIST_MEMBER_BY_ID: (listId: string, memberId: string): string =>
      `/api/mineminer/lists/${listId}/members/${memberId}`,
    SIGNAL_STATUS_CURRENT: (violationNo: string): string =>
      `/api/mineminer/signals/${encodeURIComponent(violationNo)}/status`,
    SIGNAL_STATUS_HISTORY: (violationNo: string): string =>
      `/api/mineminer/signals/${encodeURIComponent(violationNo)}/history`,
    SIGNAL_STATUS_TRANSITION: (violationNo: string): string =>
      `/api/mineminer/signals/${encodeURIComponent(violationNo)}/transition`,
    WORK_QUEUE: '/api/mineminer/work-queue',
    CAMPAIGNS: '/api/mineminer/campaigns',
    CAMPAIGN_BY_ID: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}`,
    CAMPAIGN_ENROLLMENT_SUMMARY: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/enrollment-summary`,
    CAMPAIGN_ENROLL: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/enroll`,
    CAMPAIGN_ENROLLMENTS: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/enrollments`,
    CAMPAIGN_CLICKS: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/clicks`,
    CAMPAIGN_SEND: (campaignId: string, enrollmentId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/enrollments/${enrollmentId}/send`,
    CAMPAIGN_ENROLLMENT_BY_ID: (campaignId: string, enrollmentId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/enrollments/${enrollmentId}`,
    CAMPAIGN_GO_LIVE: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/go-live`,
    CAMPAIGN_DEACTIVATE: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/deactivate`,
    CAMPAIGN_SCHEDULED_ACTIVITIES: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/scheduled-activities`,
    CAMPAIGN_SEND_WINDOW: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/send-window`,
    CAMPAIGN_ENROLLMENT_STEPS: (campaignId: string, enrollmentId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/enrollments/${enrollmentId}/steps`,
    EMAIL_TEMPLATES: '/api/mineminer/email-templates',
    EMAIL_TEMPLATE_PREVIEW: (stem: string): string =>
      `/api/mineminer/email-templates/preview?stem=${encodeURIComponent(stem)}`,
    // Sprint-110 (epic-015 Sprint B): user-authored email templates CRUD +
    // preview. Backend contract shipped by sprint-109 S2.
    USER_EMAIL_TEMPLATES: '/api/mineminer/user-email-templates',
    USER_EMAIL_TEMPLATE_BY_ID: (id: string): string =>
      `/api/mineminer/user-email-templates/${id}`,
    USER_EMAIL_TEMPLATE_PREVIEW: (id: string): string =>
      `/api/mineminer/user-email-templates/${id}/preview`,
    // Sprint-113 (epic-015 Sprint E Stream A2): multipart image upload for
    // the rich-email editor. Backend (sprint-113 S2) returns { url } on 200
    // or { error } on 400 (MIME/oversize/validation rejections).
    USER_EMAIL_TEMPLATE_IMAGE: (id: string): string =>
      `/api/mineminer/user-email-templates/${id}/images`,
    // Sprint-128 (epic-019): multipart photo upload for booth contact capture.
    // Backend returns { url } on 200 or { error } on 400 (MIME/oversize/validation).
    CONTACT_PHOTO: (id: string): string =>
      `/api/mineminer/contacts/${id}/photos`,
    // Sprint-111 (epic-015 Sprint C): per-campaign cadence-step CRUD.
    // GET returns an empty array for an existing campaign with no steps
    // (not 404). PUT replaces the whole cadence; POST appends a single row.
    CAMPAIGN_CADENCE_STEPS: (campaignId: string): string =>
      `/api/mineminer/campaigns/${campaignId}/cadence-steps`,
    REPORTS_WEEKLY_ACTIVITY: '/api/mineminer/reports/weekly-activity',
    REPORTS_CONTACT_READINESS: '/api/mineminer/reports/contact-readiness',
    REPORTS_LIST_MEMBERSHIP: '/api/mineminer/reports/list-membership',
    REPORTS_SUPPRESSION_REVIEW: '/api/mineminer/reports/suppression-review',
    REPORTS_CAMPAIGN_ENROLLMENT: '/api/mineminer/reports/campaign-enrollment',
    // sprint-122 W3: per-campaign bounced-contact list (Bounced-count drill-in).
    REPORTS_CAMPAIGN_BOUNCED: (campaignId: string): string =>
      `/api/mineminer/reports/campaign-enrollment/${campaignId}/bounced`,
    AUDIT_SEARCH: '/api/mineminer/audit/search',
    // sprint-147 — on-site MSHA 24-month violations analytics (latest weekly
    // snapshot). Rep-level; the page reads this and renders the four lenses.
    VIOLATIONS_ANALYTICS: '/api/mineminer/violations-analytics',
    // sprint-152 (epic-020) — Sync offering ↔ CFR mapping review. Catalog GET
    // returns the offering definitions + observed counts; change-requests
    // captures a proposed mapping change (intent only, no recompute).
    OFFERINGS_CATALOG: '/api/mineminer/offerings/catalog',
    OFFERING_CHANGE_REQUESTS: '/api/mineminer/offerings/change-requests',
  },

  // MineMiner public (epic-011 Sprint D / sprint-098): anonymous
  // demo-scheduling landing-page endpoints. The link token is the only trust
  // boundary; kept separate from the role-gated MINEMINER block above.
  MINEMINER_PUBLIC: {
    DEMO_RESOLVE: (token: string): string =>
      `/api/mineminer/public/demo/${encodeURIComponent(token)}`,
    DEMO_SCHEDULE: (token: string): string =>
      `/api/mineminer/public/demo/${encodeURIComponent(token)}/schedule`,
    // sprint-279 S003 (F23 slice 3): the outside contact's step link. `action` is one of
    // acknowledge | eta | comment | done | decline | fresh-link.
    STEP: (token: string): string =>
      `/api/mineminer/public/step/${encodeURIComponent(token)}`,
    STEP_ACTION: (token: string, action: string): string =>
      `/api/mineminer/public/step/${encodeURIComponent(token)}/${action}`,
  },
} as const;

/**
 * API configuration constants
 */
export const API_CONFIG = {
  get TIMEOUT(): number {
    return getApiTimeout();
  },
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
  RETRY_STATUS_CODES: [408, 429, 500, 502, 503, 504],
} as const;
